/** Shared view pieces: the freshness bar, null-safe values, definition rows and badges. */
import type { ReadEnvelope } from "../contracts/envelope.ts";
import { h, type Child } from "./dom.ts";
import { NOT_AVAILABLE, formatDurationUs, formatInstant } from "./format.ts";

/** A value that may be absent: a dash with an accessible label, never zero (UX07). */
export function metric(text: string | null, className = "metric"): HTMLElement {
  return text === null
    ? h(
        "span",
        {
          class: `${className} metric-missing`,
          attrs: { role: "img", "aria-label": "not available" },
        },
        NOT_AVAILABLE,
      )
    : h("span", { class: className }, text);
}

/** An instant cell, IST-labelled, or the absent dash. */
export function instantCell(at: string | null): HTMLElement {
  return metric(at === null ? null : formatInstant(at));
}

/** A status chip: the text always names the status, colour only reinforces it. */
export function badge(label: string, tone: "ok" | "warn" | "bad" | "info"): HTMLElement {
  return h("span", { class: `badge badge-${tone}` }, label);
}

/**
 * A wide table in its own horizontal scroll region, so the page never scrolls sideways at 390 px
 * (U100): the region is focusable and named for the table, so a keyboard user can scroll it (UX09).
 */
export function scrollRegion(label: string, table: HTMLElement): HTMLElement {
  return h(
    "div",
    { class: "table-wrap", attrs: { role: "region", "aria-label": label, tabindex: "0" } },
    table,
  );
}

/** A definition list from label/value pairs. */
export function definitions(pairs: readonly (readonly [string, Child])[]): HTMLElement {
  const list = h("dl", { class: "definitions" });
  for (const [label, value] of pairs) {
    list.append(h("dt", {}, label), h("dd", {}, value));
  }
  return list;
}

export interface FreshnessInput {
  readonly streamSequence: number | null;
}

/** The freshness bar every view renders from its envelope (UI spec §3, ruling 0.5). */
export function renderFreshnessBar(
  envelope: ReadEnvelope<unknown>,
  input: FreshnessInput = { streamSequence: null },
): HTMLElement {
  const versions = [...envelope.entityVersions.values()];
  const newest = versions.length === 0 ? null : Math.max(...versions);
  return h(
    "section",
    {
      class: "freshness-bar",
      attrs: { "aria-label": "Data freshness", "data-freshness": envelope.freshness },
    },
    definitions([
      ["Source time", instantCell(envelope.sourceTime)],
      ["Available time", instantCell(envelope.availableTime)],
      ["Server snapshot", instantCell(envelope.snapshotTime)],
      [
        "Stream sequence",
        metric(input.streamSequence === null ? null : String(input.streamSequence)),
      ],
      ["Snapshot watermark", metric(String(envelope.watermark))],
      ["Entity version", metric(newest === null ? null : `v${String(newest)}`)],
      ["Mode", metric(envelope.mode)],
      [
        "Data age",
        metric(
          envelope.ageUs === null
            ? null
            : `${formatDurationUs(envelope.ageUs)} (${envelope.freshness})`,
        ),
      ],
    ]),
  );
}
